import React from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { artists as allArtists } from "../../mock";
import { resolveMedia } from "../../lib/api";
import { Body, Countdown, CtaLink, SectionHeading } from "./primitives";
import FormBlock from "./FormBlock";

/* Each landing-page block type is its own small component; `BLOCK_COMPONENTS` maps type -> component. */

const Section = ({ type, className = "container-x py-16 lg:py-24", children }) => (
  <section className={className} data-testid={`lp-block-${type}`}>
    {children}
  </section>
);

const TextBlock = ({ block }) => (
  <Section type="text">
    <div className="grid gap-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeading>{block.heading}</SectionHeading>
      </div>
      <div className="lg:col-span-7">
        <Body>{block.body}</Body>
      </div>
    </div>
  </Section>
);

const OfferBlock = ({ block, onCta }) => (
  <Section type="offer">
    <div className="relative overflow-hidden border border-primary/40 bg-card p-8 md:p-14">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
      {block.badge && (
        <span className="inline-block bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-primary-foreground">{block.badge}</span>
      )}
      <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-8">
          <SectionHeading>{block.heading}</SectionHeading>
          <Body className="mt-5 max-w-2xl">{block.body}</Body>
          {block.expires_at && (
            <div className="mt-8">
              <Countdown expiresAt={block.expires_at} />
            </div>
          )}
        </div>
        <div className="lg:col-span-4 lg:justify-self-end">
          <CtaLink label={block.cta_label} url={block.cta_url} onCta={onCta} />
        </div>
      </div>
    </div>
  </Section>
);

const ArtistTile = ({ artist, onCta }) => (
  <a
    href={artist.bookingUrl}
    target="_blank"
    rel="noreferrer noopener"
    onClick={(e) => onCta && onCta(`Artist: ${artist.name}`, artist.bookingUrl, e)}
    className="group block"
    data-testid={`lp-artist-${artist.id}`}
  >
    <div className="relative aspect-[3/2] overflow-hidden bg-card">
      <img src={artist.image} alt={artist.name} loading="lazy" className="artist-img h-full w-full object-cover grayscale" />
      <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-70" />
    </div>
    <div className="mt-3 flex items-start justify-between gap-3 border-t border-border pt-3">
      <div>
        <h3 className="display text-2xl uppercase tracking-wide transition-colors group-hover:text-primary">{artist.name}</h3>
        <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{artist.specialty}</p>
      </div>
      <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
    </div>
  </a>
);

const ArtistsBlock = ({ block, onCta }) => {
  const chosen = block.artist_ids?.length ? allArtists.filter((a) => block.artist_ids.includes(a.id)) : allArtists.slice(0, 6);
  return (
    <Section type="artists">
      <SectionHeading>{block.heading || "Featured Artists"}</SectionHeading>
      <Body className="mt-4 max-w-2xl">{block.body}</Body>
      <div className="mt-12 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {chosen.map((a) => (
          <ArtistTile key={a.id} artist={a} onCta={onCta} />
        ))}
      </div>
    </Section>
  );
};

const FeaturesBlock = ({ block }) => (
  <Section type="features">
    <SectionHeading>{block.heading}</SectionHeading>
    <Body className="mt-4 max-w-2xl">{block.body}</Body>
    <div className="mt-12 grid gap-px bg-border/60 sm:grid-cols-2 lg:grid-cols-3">
      {(block.items || []).map((item, i) => (
        <div key={item.id || `${item.title}-${i}`} className="bg-background p-7 transition-colors hover:bg-card">
          <span className="flex h-9 w-9 items-center justify-center border border-border text-primary">
            <Check className="h-4 w-4" />
          </span>
          <h3 className="display mt-5 text-2xl uppercase tracking-wide">{item.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
        </div>
      ))}
    </div>
  </Section>
);

const ImageBlock = ({ block }) => {
  if (!block.image_url) return null;
  return (
    <Section type="image" className="container-x py-10 lg:py-16">
      <figure>
        <div className="overflow-hidden bg-card">
          <img src={resolveMedia(block.image_url)} alt={block.heading || ""} loading="lazy" className="h-auto w-full object-cover" />
        </div>
        {(block.heading || block.body) && (
          <figcaption className="mt-4 text-sm text-muted-foreground">
            {block.heading && <span className="font-semibold text-foreground">{block.heading} </span>}
            {block.body}
          </figcaption>
        )}
      </figure>
    </Section>
  );
};

const CtaBlock = ({ block, onCta }) => (
  <Section type="cta" className="bg-foreground text-background">
    <div className="container-x grid gap-10 py-20 lg:grid-cols-12 lg:items-center lg:py-28">
      <div className="lg:col-span-8">
        <SectionHeading>{block.heading}</SectionHeading>
        <Body className="mt-5 max-w-2xl text-background/70">{block.body}</Body>
      </div>
      <div className="lg:col-span-4 lg:justify-self-end">
        <CtaLink label={block.cta_label} url={block.cta_url} onCta={onCta} />
      </div>
    </div>
  </Section>
);

export const BLOCK_COMPONENTS = {
  text: TextBlock,
  offer: OfferBlock,
  artists: ArtistsBlock,
  features: FeaturesBlock,
  image: ImageBlock,
  cta: CtaBlock,
  form: FormBlock,
};

/** Renders one block by type; unknown types render nothing. */
const Block = ({ block, onCta, slug }) => {
  const Component = BLOCK_COMPONENTS[block.type];
  return Component ? <Component block={block} onCta={onCta} slug={slug} /> : null;
};

export default Block;
